/**
 * Shared camera filter preference for preview + future CallRoom (Phase 6B).
 * localStorage only — no Firestore, no streams/images.
 */

const STORAGE_KEY = "livora:cameraFilterSettings";
const LEGACY_SNAP_KEY = "livora:snap:selectedFilterId";

/** @typedef {{ id: string, provider: "snap"|"beauty", intensity: number, updatedAt: number }} CameraFilterSettings */

/** @type {CameraFilterSettings} */
export const DEFAULT_CAMERA_FILTER = Object.freeze({
  id: "none",
  provider: "snap",
  intensity: 100,
  updatedAt: 0,
});

const VALID_SNAP_IDS = new Set([
  "none",
  "snow_radiance",
  "glow_whitening",
  "cartoon_camera",
  "luminous_femme",
]);

const VALID_BEAUTY_IDS = new Set([
  "none",
  "original",
  "velvet",
  "glow",
  "soft",
  "clean",
  "studio",
]);

/**
 * @param {unknown} value
 * @returns {CameraFilterSettings}
 */
export function normalizeCameraFilter(value) {
  const base = {
    id: DEFAULT_CAMERA_FILTER.id,
    provider: DEFAULT_CAMERA_FILTER.provider,
    intensity: DEFAULT_CAMERA_FILTER.intensity,
    updatedAt: Date.now(),
  };

  if (!value || typeof value !== "object") {
    return { ...base };
  }

  const raw = /** @type {Record<string, unknown>} */ (value);
  const provider = raw.provider === "beauty" ? "beauty" : "snap";

  let id = String(raw.id || DEFAULT_CAMERA_FILTER.id).trim().toLowerCase();
  if (id === "no_filter") {
    id = "none";
  }

  const validSet = provider === "beauty" ? VALID_BEAUTY_IDS : VALID_SNAP_IDS;
  if (!validSet.has(id)) {
    id = "none";
  }

  let intensity = Number(raw.intensity);
  if (!Number.isFinite(intensity)) {
    intensity = DEFAULT_CAMERA_FILTER.intensity;
  }
  intensity = Math.min(100, Math.max(0, Math.round(intensity)));

  const updatedAt = Number(raw.updatedAt);
  return {
    id,
    provider,
    intensity,
    updatedAt: Number.isFinite(updatedAt) && updatedAt > 0 ? updatedAt : Date.now(),
  };
}

/**
 * @returns {CameraFilterSettings}
 */
export function getSavedCameraFilter() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return normalizeCameraFilter(JSON.parse(raw));
    }
  } catch {
    // fall through to legacy migration
  }

  try {
    const legacyId = localStorage.getItem(LEGACY_SNAP_KEY);
    if (legacyId) {
      const id = legacyId === "no_filter" ? "none" : String(legacyId).trim();
      if (VALID_SNAP_IDS.has(id)) {
        return normalizeCameraFilter({
          id,
          provider: "snap",
          intensity: DEFAULT_CAMERA_FILTER.intensity,
          updatedAt: Date.now(),
        });
      }
    }
  } catch {
    // ignore
  }

  return { ...DEFAULT_CAMERA_FILTER, updatedAt: 0 };
}

/**
 * @param {Partial<CameraFilterSettings>} partial
 * @returns {CameraFilterSettings}
 */
export function saveCameraFilter(partial) {
  const current = getSavedCameraFilter();
  const next = normalizeCameraFilter({ ...current, ...partial, updatedAt: Date.now() });

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // quota / private mode
  }

  try {
    const legacySnapId = next.provider === "snap"
      ? (next.id === "none" ? "no_filter" : next.id)
      : "no_filter";
    localStorage.setItem(LEGACY_SNAP_KEY, legacySnapId);
  } catch {
    // ignore
  }

  return next;
}

export function clearSavedCameraFilter() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_SNAP_KEY);
  } catch {
    // ignore
  }
}

/**
 * @param {CameraFilterSettings} settings
 * @returns {string}
 */
export function toSnapFilterId(settings) {
  if (!settings || settings.provider !== "snap") {
    return "no_filter";
  }
  return settings.id === "none" ? "no_filter" : settings.id;
}

/**
 * @param {string} snapFilterId
 * @param {number} [intensity]
 * @returns {CameraFilterSettings}
 */
export function fromSnapFilterId(snapFilterId, intensity = DEFAULT_CAMERA_FILTER.intensity) {
  const id = snapFilterId === "no_filter" ? "none" : String(snapFilterId || "none").trim();
  return normalizeCameraFilter({
    id,
    provider: "snap",
    intensity,
  });
}
